import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Paper,
  Divider,
  Grid,
  Container,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DateTime } from 'luxon';
import ModalMAIQ from 'app/components/ModalMAIQ';
import FileUploadMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/FileUploadMAIQ';
import { useDataIngestion } from 'app/context/DataIngestionContext';
import { useSelector, useDispatch } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { useRunLogs } from 'app/context/RunLogsContext';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { useUploadModalDefaults } from 'app/pages/RunLogsPage/hooks/useUploadModalDefaults';
import { getDefaultUploadDateRange } from 'app/pages/RunLogsPage/utils/uploadModalDefaults';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CancelIcon from '@mui/icons-material/Cancel';

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({
  open,
  onClose,
  onUploadSuccess,
}) => {
  const dispatch = useDispatch();
  const [provider, setProvider] = useState('');
  const [leadType, setLeadType] = useState('');
  const [dateRange, setDateRange] = useState<[any, any]>([null, null]);
  const [waitForUpload, setWaitForUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isPullMode, setIsPullMode] = useState(false);

  const {
    uploadData,
    loading,
    error: contextError,
    success,
    setError,
  } = useDataIngestion();

  const { subscribeToRunLog, socketConnected } = useRunLogs();

  // Get selected dealer using the Header selector
  const selectedDealer = useSelector(selectDealerSelected);
  // Extract dealer ID using same pattern as SalesDemoPage
  const dealerId = selectedDealer?.dealer || '';
  const hasDealerSelected = !!dealerId;

  const {
    providerOptions,
    defaultProvider,
    defaultDateRange,
    loading: loadingCrmDefaults,
  } = useUploadModalDefaults(open, dealerId);

  // Combine local and context errors
  const error = localError || contextError;

  // Function to reset all states
  const resetAllStates = () => {
    const [start, end] = getDefaultUploadDateRange();
    setProvider('');
    setLeadType('');
    setDateRange([start, end]);
    setWaitForUpload(false);
    setSelectedFile(null);
    setLocalError(null);
    setError('');
    setIsPullMode(false);
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    setDateRange(defaultDateRange);
  }, [open, defaultDateRange]);

  useEffect(() => {
    if (!open || !defaultProvider) {
      return;
    }

    setProvider(defaultProvider);
  }, [open, defaultProvider]);

  // Modified close handler that resets states
  const handleClose = () => {
    resetAllStates();
    onClose();
  };

  // Clear local error when modal is closed
  useEffect(() => {
    if (!open) {
      resetAllStates();
    }
  }, [open]);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    // Clear previous errors
    setLocalError(null);
    setError('');

    if (!isPullMode && !selectedFile) {
      setLocalError('Please select a file to upload');
      return;
    }

    if (!dealerId) {
      setLocalError('Please select a dealer first');
      return;
    }
    console.log('selectedFile', selectedFile);

    // Create FormData object
    const formData = new FormData();

    // Only append file if not in pull mode
    if (!isPullMode && selectedFile) {
      formData.append('file', selectedFile);
    }

    formData.append('provider', provider);
    formData.append('lead_type', leadType);

    // Add ingestion_type and ingestion_provider parameters
    if (isPullMode) {
      formData.append('ingestion_type', 'pull');
      formData.append('ingestion_provider', 'manual');
    } else {
      formData.append('ingestion_type', 'push');
      formData.append('ingestion_provider', 'upload');
    }

    // Format dates in required format (YYYY-MM-DD)
    if (dateRange[0]) {
      const startDate = dateRange[0].toFormat('yyyy-MM-dd');
      formData.append('start_date', startDate);
    }

    if (dateRange[1]) {
      const endDate = dateRange[1].toFormat('yyyy-MM-dd');
      formData.append('end_date', endDate);
    }

    try {
      const runId = await uploadData(dealerId, formData, runId => {
        // This callback is called after successful upload (with 2-second delay from context)
        console.log('🚀 Upload completed, Run ID:', runId);

        // Subscribe to socket updates for this run
        if (runId && socketConnected) {
          console.log('📡 Subscribing to socket updates for run:', runId);
          subscribeToRunLog(runId);
        }

        if (onUploadSuccess) {
          onUploadSuccess();
        }
      });

      // If we got a run ID immediately, subscribe to socket updates
      if (runId && socketConnected) {
        subscribeToRunLog(runId);
      }

      // Close modal only after a successful upload
      resetAllStates();
      onClose();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to upload data';
      console.error('❌ Upload failed:', error);
      setLocalError(message);
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message,
        }),
      );
    }
  };

  const isFormValid = (): boolean => {
    const baseValidation =
      !!provider &&
      !!leadType &&
      !!dateRange[0] &&
      !!dateRange[1] &&
      !!dealerId;

    if (isPullMode) {
      // In pull mode, file is not required
      return baseValidation;
    } else {
      // In upload mode, file is required
      return baseValidation && !!selectedFile;
    }
  };

  return (
    <ModalMAIQ open={open} onClose={handleClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{
          width: '800px',
          maxWidth: '90vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: '#0F2A52',
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Typography variant="h6" fontWeight="600">
              Upload Data
            </Typography>
            <Box>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    color: 'white',
                    p: 0.5,
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Typography
            variant="subtitle2"
            sx={{ mt: 0.5, fontWeight: 400, fontSize: '0.8rem' }}
          >
            Upload CRM data for dealer:{' '}
            {selectedDealer?.name ||
              selectedDealer?.abbreviation ||
              dealerId ||
              'No dealer selected'}
          </Typography>
        </Box>

        {/* Content area with scrolling */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            p: 3,
          }}
        >
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                '& .MuiAlert-message': {
                  width: '100%',
                },
              }}
            >
              <Typography variant="subtitle2" fontWeight="600">
                Upload Failed
              </Typography>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}

          {!hasDealerSelected && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Please select a dealer from the header dropdown
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                CRM Provider *
              </Typography>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={provider}
                  onChange={e => setProvider(e.target.value)}
                  displayEmpty
                  disabled={loadingCrmDefaults}
                  inputProps={{
                    sx: { py: 1.5 },
                  }}
                >
                  <MenuItem value="" disabled>
                    {loadingCrmDefaults
                      ? 'Loading CRM providers...'
                      : 'Select CRM Provider'}
                  </MenuItem>
                  {providerOptions.map(option => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Lead Cohort Type *
              </Typography>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={leadType}
                  onChange={e => setLeadType(e.target.value)}
                  displayEmpty
                  inputProps={{
                    sx: { py: 1.5 },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Lead Cohort Type
                  </MenuItem>
                  <MenuItem value="dormant">Dormant</MenuItem>
                  <MenuItem value="lease">Lease</MenuItem>
                  <MenuItem value="sold">Sold</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Date Range *
              </Typography>
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <DateRangePicker
                  value={dateRange}
                  onChange={newValue => setDateRange(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      variant: 'outlined',
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isPullMode}
                    onChange={e => setIsPullMode(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Manual Trigger
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      Enable manual trigger to pull data instead of uploading a
                      file
                    </Typography>
                  </Box>
                }
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    width: '100%',
                  },
                }}
              />
            </Grid>

            {!isPullMode && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  File Upload *
                </Typography>
                <FileUploadMAIQ
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".csv,.xlsx,.xls,.pdf"
                  id="upload-data-ingestion-file"
                />
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
                >
                  Accepted file formats: CSV, Excel, PDF
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider />

        {/* Footer with action buttons - fixed at bottom */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: 9,
            flexShrink: 0,
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              color: '#0F2A52',
              borderColor: '#0F2A52',
              py: 0.75,
              px: 3,
              '&:hover': {
                borderColor: '#0F2A52',
                bgcolor: 'rgba(15, 42, 82, 0.04)',
              },
              minWidth: '100px',
              height: '40px',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!isFormValid() || loading}
            startIcon={loading ? undefined : <UploadFileIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              bgcolor: '#0F2A52',
              color: 'white',
              py: 0.75,
              px: 3,
              '&:hover': {
                bgcolor: '#0a1d38',
              },
              minWidth: '100px',
              height: '40px',
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                Uploading...
              </Box>
            ) : (
              'Upload'
            )}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        message="Upload successful!"
      />
    </ModalMAIQ>
  );
};

export default UploadModal;
